/** @format */

/* eslint react-hooks/rules-of-hooks: "warn" */

/* cspell: disable */
import isPropValid from '@emotion/is-prop-valid';
import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '@mpb-com/feather/components/atoms/button';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import {
    HeadingColor,
    HeadingSize,
} from '@mpb-com/feather/components/atoms/heading/types';
import { IntersectionObserver } from '@mpb-com/feather/components/atoms/intersection-observer';
import {
    LogoVariant,
    MpbLogo,
    MpbLogoSize,
} from '@mpb-com/feather/components/atoms/mpb-logo';
import React, {
    Fragment,
    FunctionComponent,
    RefObject,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import styled, { createGlobalStyle } from 'styled-components';

import { getConfig } from '@toucan/common/services/nextjs/settings';

import ActionCamera from '@toucan/client/global/images/kit/action-camera.svg';
import Camera from '@toucan/client/global/images/kit/camera.svg';
import Drone from '@toucan/client/global/images/kit/drone.svg';
import DslrCamera from '@toucan/client/global/images/kit/dslr-camera.svg';
import Flash from '@toucan/client/global/images/kit/flash.svg';
import InstantCamera from '@toucan/client/global/images/kit/instant-camera.svg';
import Lens from '@toucan/client/global/images/kit/lens.svg';

import { MediaQuery, useMediaQuery } from '@toucan/client/global/application';
import { MarketCode } from '@toucan/client/global/market';

import { styles } from './error-500.styles';
import { useTranslationByMarket } from './hooks/use-translation-by-market';

const StyledError500 = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export type Error500Props = {
    statusCode: number;
    refCode: string;
    activeMarketCode: MarketCode;
};

type BackgroundImageArrProps = {
    contentRef: RefObject<HTMLDivElement>;
};

const calculateImageSize = (): string => {
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);
    const isMobile = useMediaQuery(MediaQuery.SMALL_MOBILE);

    if (isSmallTablet && !isMobile) {
        return '57px';
    }

    if (isMobile) {
        return '36px';
    }

    return '80px';
};

const BackgroundImageArr = ({ contentRef }: BackgroundImageArrProps) => {
    const [imageElementsCount, setImageElementsCount] = useState(20);
    const [windowSize, setWindowSize] = useState({ height: 1000, width: 1000 });

    useLayoutEffect(() => {
        function updateSize() {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        }
        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useLayoutEffect(() => {
        const el = document.querySelector('.background-image--container');

        if (el) {
            const elementStyles = window.getComputedStyle(el);
            const totalElementWidth =
                el.clientWidth +
                parseInt(elementStyles.getPropertyValue('margin-left')) +
                parseInt(elementStyles.getPropertyValue('margin-right'));
            const totalElementHeight =
                el.clientHeight +
                parseInt(elementStyles.getPropertyValue('margin-top')) +
                parseInt(elementStyles.getPropertyValue('margin-bottom'));

            const totalElementArea = totalElementWidth * totalElementHeight;

            const elementCount =
                ((windowSize.height * windowSize.width) / totalElementArea) * 2;
            setImageElementsCount(elementCount);
        }
    }, [windowSize]);

    const imagesArr = [
        ActionCamera,
        Camera,
        DslrCamera,
        Drone,
        Flash,
        InstantCamera,
        Lens,
    ];

    const intersectionObserverOptions = {
        root: contentRef?.current,
        threshold: 0.1,
    };

    const getRandomNumber = (num: number) => Math.floor(Math.random() * num);

    const backgroundImageSize = calculateImageSize();

    const backgroundImageArr = Array.from({ length: imageElementsCount }).map(
        (_, i) => {
            const Image = imagesArr[i % imagesArr.length];

            return (
                <div
                    className="background-image--container"
                    style={{
                        animationDelay: `${getRandomNumber(11)}s`,
                    }}
                    key={i}
                >
                    <IntersectionObserver {...intersectionObserverOptions}>
                        {({ isIntersecting }: { isIntersecting: boolean }) => {
                            return (
                                <Image
                                    style={{
                                        height: `${backgroundImageSize}`,
                                        width: `${backgroundImageSize}`,
                                    }}
                                    className={`${
                                        isIntersecting &&
                                        'background-image--item--intersecting'
                                    }`}
                                />
                            );
                        }}
                    </IntersectionObserver>
                </div>
            );
        }
    );

    return <Fragment>{backgroundImageArr}</Fragment>;
};

const BackgroundBodyColor = createGlobalStyle`
  body,html {
    background-color: var(--colors-text-secondary);
  }
`;

export const Error500: FunctionComponent<Error500Props> = ({
    statusCode,
    refCode,
    activeMarketCode,
}) => {
    const { publicRuntimeConfig } = getConfig();

    const helpDeskLink = publicRuntimeConfig.intercomLinks[activeMarketCode];

    // We don't have access to translations so we need to hardcode them ourselves
    // There is a way to fetch, however, we don't want to add extra complications to this page
    const translations = useTranslationByMarket(activeMarketCode, refCode);

    const contentRef = useRef<HTMLDivElement | null>(null);

    return (
        <Fragment>
            <BackgroundBodyColor />
            <StyledError500
                activeMarketCode={activeMarketCode}
                statusCode={statusCode}
                refCode={refCode}
            >
                <div className="content-container" ref={contentRef}>
                    <MpbLogo
                        variant={LogoVariant.MONO}
                        logoSize={MpbLogoSize.LARGE}
                        className="logo"
                    />
                    <div className="content-container--opaque">
                        <div className="content-headers">
                            <Heading
                                size={HeadingSize.H2}
                                className="heading-ref"
                                color={HeadingColor.TEXT_INVERSE}
                            >
                                {translations.preHeading}
                            </Heading>

                            <Heading
                                size={HeadingSize.H1}
                                color={HeadingColor.TEXT_INVERSE}
                            >
                                {translations.heading}
                            </Heading>
                        </div>
                        <div className="content-copy">
                            {translations.message}
                        </div>
                        <div className="content-buttons">
                            {helpDeskLink && (
                                <Button
                                    className="content-buttons--button"
                                    size={ButtonSize.SMALL}
                                    data-testid="error-500__help-centre-button"
                                    variant={ButtonVariant.SECONDARY}
                                    href={helpDeskLink}
                                    target="_blank"
                                >
                                    {translations.helpCentre}
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="background-container">
                        <BackgroundImageArr contentRef={contentRef} />
                    </div>
                </div>
            </StyledError500>
        </Fragment>
    );
};
